import { checkNever } from "portal/utils/identity";
import { DataRow, RowType } from "./table.types";
import {
  gridFilteredDescendantCountLookupSelector,
  GridRenderCellParams,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import { useTranslation } from "react-i18next";
import { valueOrLoading } from "portal/utils/strings";
import CollapsedIcon from "@mui/icons-material/ChevronRightOutlined";
import ExpandedIcon from "@mui/icons-material/ExpandMoreOutlined";
import React, { FunctionComponent, MouseEventHandler } from "react";

/**
 * Tree “grouping” column for the reports grid: expand/collapse affordance,
 * human-readable label (serial, date, job name, or total/average), and small
 * total/average hints when the group has child rows.
 */
export const CarbonGroupingCell: FunctionComponent<
  GridRenderCellParams<DataRow> & {
    showTotals: boolean;
    showAverages: boolean;
  }
> = ({ id, field, row, rowNode, showTotals, showAverages }) => {
  const { t } = useTranslation();
  const apiRef = useGridApiContext();
  const filteredDescendantCountLookup = useGridSelector(
    apiRef,
    gridFilteredDescendantCountLookupSelector
  );
  const filteredDescendantCount =
    filteredDescendantCountLookup[rowNode.id] ?? 0;

  const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
    if (rowNode.type !== "group") {
      return;
    }

    apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
    apiRef.current.setCellFocus(id, field);
    event.stopPropagation();
  };

  let label: string;
  switch (row.type) {
    case RowType.SINGLE_JOB: {
      label = row.jobName;
      break;
    }
    case RowType.SINGLE_DAY: {
      label = row.date;
      break;
    }
    case RowType.ROBOT_AGGREGATES: {
      const { customerSerial } = row;
      label = valueOrLoading(customerSerial, t);
      break;
    }
    case RowType.BOTTOM_LINE_AGGREGATES: {
      label = t(row.aggregationType);
      break;
    }
    default: {
      console.error("Unexpected row type in grouping cell:", checkNever(row));
      label = "";
    }
  }

  return (
    <div
      style={{ marginLeft: rowNode.depth * 50 }}
      onClick={handleClick}
      tabIndex={-1}
      role="button"
      className="cursor-pointer flex gap-2 items-center w-full h-full"
    >
      {filteredDescendantCount > 0 && (
        <>
          {rowNode.type === "group" && rowNode.childrenExpanded ? (
            <ExpandedIcon />
          ) : (
            <CollapsedIcon />
          )}
        </>
      )}
      <span>{label}</span>
      {filteredDescendantCount > 0 && (
        <div className="flex flex-1 flex-col h-full items-end font-mono text-xs opacity-50 font-normal">
          {showTotals && (
            <span className="flex-1 flex items-center">
              {t("views.reports.scheduled.table.fields.totalShort")}
            </span>
          )}
          {showAverages && (
            <span className="flex-1 flex items-center">
              {t("views.reports.scheduled.table.fields.averageShort")}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
